/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { CompanyTableHeader } from '../../components/company/CompanyTableHeader';
import { CompanyRow } from '../../components/company/CompanyRow';
import { CompanyService } from '../../services/company.service';
import { CompanyType } from '../../types/CompanyType';
import { CompanyModal } from '../../components/company/CompanyModal';
import { UserModal } from '../../components/user/UserModal';

export const CompaniesPage = () => {
  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyType | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [targetCompanyForUser, setTargetCompanyForUser] = useState<CompanyType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadCompanies = async () => {
    try {
      setIsLoading(true);
      const res = await CompanyService.getCompanies();
      setCompanies(res.data);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingCompany(null);
  };

  const changeStatus = async (company: any) => {
    try {
      const res = await CompanyService.toggleStatus(company.id);
      setCompanies((prev) => prev.map((c) => (c.id === company.id ? res.data : c)));
      loadCompanies();
    } catch (err) {
      console.error('Failed to toggle company status:', err);
    }
  };

  return (
    <div>
      <h1 className="text-4xl font-bold text-center sm:pb-4 sm:text-left text-primary">
        Companies
      </h1>

      <div className="flex justify-center sm:justify-start">
        <Button
          icon={<Plus />}
          className="px-16 py-3 mt-4 sm:px-6 sm:mt-0"
          onClick={() => {
            setEditingCompany(null);
            setIsModalOpen(true);
          }}
        >
          New
        </Button>
      </div>

      <div className="mt-8">
        {isLoading ? (
          <p className="mt-6 text-xl text-center text-muted-foreground">Loading...</p>
        ) : companies.length === 0 ? (
          <p className="mt-6 text-xl text-center text-muted-foreground">No companies found.</p>
        ) : (
          <>
            <CompanyTableHeader />
            {companies.map((company) => (
              <CompanyRow
                key={company.id}
                company={company}
                onEdit={() => {
                  setEditingCompany(company);
                  setIsModalOpen(true);
                }}
                onAddUser={() => {
                  setTargetCompanyForUser(company);
                  setIsUserModalOpen(true);
                }}
                onToggleStatus={() => changeStatus(company)}
              />
            ))}
          </>
        )}
      </div>

      <CompanyModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={() => {
          loadCompanies();
          handleModalClose();
        }}
        editingCompany={editingCompany}
      />
      <UserModal
        isOpen={isUserModalOpen}
        onClose={() => {
          setIsUserModalOpen(false);
          setTargetCompanyForUser(null);
        }}
        onSuccess={() => {
          loadCompanies();
          setIsUserModalOpen(false);
          setTargetCompanyForUser(null);
        }}
        lockedCompanyId={targetCompanyForUser?.id}
        lockedRoleName="COMPANY_ADMIN"
      />
    </div>
  );
};
